import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DiagnosticInput } from '../../models/diagnostic.models';

interface ParsedUrl {
  ok: boolean;
  storeId?: string;
  storeType?: string;
  error?: string;
}

@Component({
  selector: 'app-diagnostic-form',
  templateUrl: './diagnostic-form.component.html',
  styleUrls: ['./diagnostic-form.component.scss']
})
export class DiagnosticFormComponent implements OnInit, OnChanges {
  @Input() loading = false;
  @Input() country = '';
  @Output() submitted = new EventEmitter<DiagnosticInput>();

  form!: FormGroup;
  parsedUrl: ParsedUrl = { ok: false };

  readonly campaignTypes = [
    { id: 'sponsored_products', name: 'Sponsored Products' },
    { id: 'banners', name: 'Banners' },
    { id: 'order_tracker', name: 'Order Tracker' },
    { id: 'turbo_banner', name: 'Turbo Banner' }
  ];

  constructor(private readonly fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      campaignType: ['', Validators.required],
      campaignId: ['', Validators.required],
      storeUrl: [''],
      keyword: ['']
    });

    this.form.controls['storeUrl'].valueChanges.subscribe(val => {
      this.parsedUrl = this.parseRappiUrl(val);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loading && this.form) {
      if (this.loading) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    }
  }

  get campaignType(): string {
    return this.form ? this.form.controls['campaignType'].value : '';
  }

  get needsUrl(): boolean {
    return this.campaignType !== 'order_tracker';
  }

  get needsKeyword(): boolean {
    return ['banners', 'sponsored_products'].includes(this.campaignType);
  }

  get storeUrl(): string {
    return this.form ? (this.form.controls['storeUrl'].value || '') : '';
  }

  get showUrlError(): boolean {
    return this.needsUrl && !!this.storeUrl && !this.parsedUrl.ok;
  }

  get canSubmit(): boolean {
    if (this.loading) {
      return false;
    }
    if (!this.country) {
      return false;
    }
    if (!this.form.controls['campaignType'].value) {
      return false;
    }
    if (!this.form.controls['campaignId'].value?.trim()) {
      return false;
    }
    if (this.needsUrl && !this.parsedUrl.ok) {
      return false;
    }
    return true;
  }

  onTypeSelect(item: any): void {
    this.form.patchValue({ campaignType: item?.id || '' });
    if (item?.id === 'order_tracker') {
      this.form.patchValue({ storeUrl: '' });
      this.parsedUrl = { ok: false };
    }
  }

  onSubmit(): void {
    if (!this.canSubmit) {
      return;
    }
    const { campaignType, campaignId, keyword } = this.form.value;
    this.submitted.emit({
      campaignId: campaignId.trim(),
      country: this.country,
      storeId: this.needsUrl ? (this.parsedUrl.storeId || '') : '',
      storeType: this.needsUrl ? (this.parsedUrl.storeType || '') : '',
      campaignType,
      keyword: this.needsKeyword ? (keyword?.trim() || null) : null
    });
  }

  private parseRappiUrl(rawUrl: string): ParsedUrl {
    const url = (rawUrl || '').trim();
    if (!url) {
      return { ok: false, error: 'URL vacía' };
    }
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return { ok: false, error: 'URL inválida' };
    }
    if (!/(^|\.)rappi\.(com(\.[a-z]{2})?|[a-z]{2,3})$/i.test(parsed.hostname)) {
      return { ok: false, error: 'La URL debe ser de Rappi' };
    }
    const parts = parsed.pathname.split('/').filter(Boolean);
    const idSlugSegment = parts.find((part) => /^\d+(-|$)/.test(part)) || '';
    if (!idSlugSegment) {
      return { ok: false, error: 'La URL no contiene un store id valido' };
    }
    const dashIdx = idSlugSegment.indexOf('-');
    const storeId = dashIdx !== -1 ? idSlugSegment.slice(0, dashIdx).trim() : idSlugSegment.trim();
    const storeType = dashIdx !== -1 ? idSlugSegment.slice(dashIdx + 1).trim().replace(/-/g, '_') : '';
    if (!/^\d+$/.test(storeId)) {
      return { ok: false, error: 'No se pudo extraer el Store ID de la URL' };
    }
    return { ok: true, storeId, storeType };
  }
}
